<template>
    <application-layout>
        <v-main>
            <v-container v-if="survey">
                <h1 class="mb-4">Edit "{{ survey.name }}" Survey</h1>
                <div class="ml-0 mr-3 mt-4">
                    <v-row class="mx-0">
                        <v-col cols="6" class="pl-0">
                            <app-input
                                data-testid="surveys-edit-title-input"
                                class="surveys-edit-title-input"
                                :type="'text'"
                                label="Survey Title"
                                v-model="survey.name"
                                hide-details
                            />
                        </v-col>
                        <v-col cols="6" class="pr-0">
                            <app-input
                                data-testid="surveys-edit-slug-input"
                                class="surveys-edit-slug-input"
                                :type="'text'"
                                label="Slug"
                                disabled
                                v-model="survey.slug"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    <h2 class="mt-3">Survey Questions</h2>
                    <v-dialog
                        v-model="addModal"
                        persistent
                        max-width="600"
                    >
                        <template v-slot:activator="{ on }">
                            <v-btn
                                data-testid="surveys-edit-add-survey-btn"
                                class="surveys-edit-add-survey-btn mt-2 mb-1 mx-0"
                                v-on="on"
                                color="primary"
                            >
                                <v-icon>add</v-icon>
                                Add Survey Question
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-title class="headline">Add Survey Question</v-card-title>
                            <v-divider />
                            <v-form>
                                <v-card-text>
                                    <app-input
                                        data-testid="surveys-edit-add-survey-question-input"
                                        class="surveys-edit-add-survey-question-input"
                                        :type="'text'"
                                        label="Question"
                                        v-model="newSurveyQuestion.text"
                                    />
                                    <v-select
                                        data-testid="surveys-edit-add-survey-categories-select"
                                        class="surveys-edit-add-survey-categories-select"
                                        clearable
                                        filled
                                        label="Type"
                                        :items="surveyCategories"
                                        v-model="newSurveyQuestion.type"
                                    />
                                </v-card-text>
                                <v-card-actions>
                                    <v-btn
                                      data-testid="surveys-edit-cancel-btn"
                                      class="surveys-edit-cancel-btn"
                                      color="error"
                                      @click="closeAddModal"
                                    >
                                      Cancel
                                    </v-btn>
                                    <v-spacer />
                                    <v-btn
                                        data-testid="surveys-edit-add-question-btn"
                                        class="surveys-edit-add-question-btn"
                                        color="primary"
                                        :disabled="!newSurveyQuestion.text || !newSurveyQuestion.type"
                                        @click="addSurveyQuestion"
                                    >
                                        Add Question
                                    </v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-card>
                    </v-dialog>
                    <transition-group
                      name="scroll-x-transition"
                      mode="out-in"
                    >
                        <div
                            v-for="(question, i) of survey.questions"
                            :key="question.id"
                            class="pb-2"
                        >
                            <v-row
                              class="mt-3 mb-2"
                              justify="space-between"
                              align="center"
                            >
                                <v-col cols="4">
                                    <app-input
                                        data-testid="surveys-edit-survey-question-input"
                                        class="surveys-edit-survey-question-input mx-2"
                                        :type="'text'"
                                        label="Survey Question"
                                        v-model="question.text"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="6" class="px-2 text-right">
                                    <v-row justify="end">
                                        <v-btn
                                            data-testid="surveys-edit-add-answer-btn"
                                            class="surveys-edit-add-answer-btn mr-3"
                                            v-if="question.type !== 'rating'"
                                            @click="addAnswer(question)"
                                            color="success"
                                        >
                                            <v-icon class="mr-2">add</v-icon>
                                            <span class="mr-2">Add Answer</span>
                                        </v-btn>
                                        <v-btn
                                          data-testid="surveys-edit-delete-question-btn"
                                          class="surveys-edit-delete-question-btn mr-3"
                                          @click="openDelete('survey question', i)"
                                          color="error"
                                        >
                                            <v-icon class="mr-2">close</v-icon>
                                            <span class="mr-2">Delete Question</span>
                                        </v-btn>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row justify="space-between" align="start">
                                <v-col cols="3">
                                    <v-select
                                        data-testid="surveys-edit-survey-categories-select"
                                        class="surveys-edit-survey-categories-select mx-2"
                                        label="Question Type"
                                        :items="surveyCategories"
                                        v-model="question.type"
                                        filled
                                        hide-details
                                        @change="toggleType(question)"
                                    />
                                </v-col>
                                <v-col cols="8">
                                    <v-row
                                        v-if="question.type === 'multiple_choice'"
                                    >
                                        <v-col
                                            cols="4"
                                            v-for="(answer, idx) of question.allowed_answers"
                                            :key="idx"
                                        >
                                            <v-text-field
                                                data-testid="surveys-edit-answer-input"
                                                class="surveys-edit-answer-input mx-2"
                                                filled
                                                clearable
                                                :label="`Answer ${idx + 1}`"
                                                v-model="answer.value"
                                                hide-details
                                            >
                                                <template v-slot:append-outer>
                                                    <v-icon
                                                        data-testid="surveys-edit-delete-answer-btn"
                                                        class="surveys-edit-delete-answer-btn mr-2 error--text"
                                                        @click="openDelete('survey answer', idx, question)"
                                                        :disabled="question.allowed_answers.length < 3"
                                                    >
                                                      delete
                                                    </v-icon>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                    </v-row>
                                    <v-row v-else class="title ml-2 mt-3">
                                        Available answers will be presented on a scale ranging from 1 (lowest value) to 5 (highest value).
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-divider class="mt-3" />
                        </div>
                    </transition-group>
                    <v-btn
                        data-testid="surveys-edit-save-survey-btn"
                        class="surveys-edit-save-survey-btn mx-0 mt-4"
                        :disabled="!canSave"
                        @click="updateSurvey"
                        color="primary"
                    >
                      Save Survey Questions
                    </v-btn>

                    <delete-modal
                        v-model="deleteModal"
                        @delete="deleteItem"
                        :itemType="itemType"
                        :advisory="advisory"
                    />
                </div>
            </v-container>
        </v-main>
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { advisoryRestorePreviousSession } from "@/helpers/warning-advisory-text";

export default {
  data() {
    return {
      advisory: advisoryRestorePreviousSession,
      addModal: false,
      deleteModal: false,
      editQuestionIndex: "",
      itemType: "",
      newSurveyQuestion: {
        text: "",
        type: "",
        id: null,
        allowed_answers: []
      },
      selectedItemIndex: "",
      selectedQuestion: "",
      surveyCategories: [
        {
          text: "Multiple Choice",
          value: "multiple_choice"
        },
        {
          text: "Rating",
          value: "rating"
        }
      ]
    };
  },

  methods: {
    addSurveyQuestion() {
      this.newSurveyQuestion.id = this.survey.questions.length + 1;
      if (this.newSurveyQuestion.type === "multiple_choice") {
        this.addMultipleChoiceFields(this.newSurveyQuestion);
      };
      this.survey.questions.push(this.newSurveyQuestion);
      this.closeAddModal();
    },

    closeAddModal() {
      this.addModal = false;
      this.newSurveyQuestion = {
        text: "",
        type: "",
        id: null,
        allowed_answers: []
      };
    },

    addMultipleChoiceFields(surveyQuestion) {
      surveyQuestion.allowed_answers = [
        {
          number: 1,
          value: ""
        },
        {
          number: 2,
          value: ""
        }
      ];
    },

    addAnswer(surveyQuestion) {
      const payload = {
        number: surveyQuestion.allowed_answers[surveyQuestion.allowed_answers.length - 1].number + 1,
        value: ""
      };
      surveyQuestion.allowed_answers.push(payload);
    },

    toggleType(question) {
      if (question.type === "multiple_choice" && !question.allowed_answers) {
        this.addMultipleChoiceFields(question);
      }
    },

    openDelete(itemType, selectedItemIndex, selectedQuestion) {
      this.deleteModal = true;
      this.itemType = itemType;
      this.selectedItemIndex = selectedItemIndex;
      this.selectedQuestion = selectedQuestion;
    },

    deleteItem() {
      if (this.itemType === "survey question") {
        this.survey.questions.splice(this.selectedItemIndex, 1);
      } else if (this.itemType === "survey answer") {
        this.selectedQuestion.allowed_answers.splice(this.selectedItemIndex, 1);
      }
      this.deleteModal = false;
      this.selectedItemIndex = "";
      this.itemType = "";
      this.selectedQuestion = "";
    },

    updateSurvey() {
      const survey = this.survey;
      this.survey.questions.forEach(question => {
        if (question.type === "rating") {
          question.allowed_answers = null; // Ensures rating q's are only posted to db with null allowed_answers
        }
      });
      this.$store.dispatch("updateSurvey", survey)
        .then(resp => {
          this.$store.dispatch("getSurvey", this.$route.params.surveyId);
        });
    }
  },

  computed: {
    survey() {
      return this.$store.state.surveys.survey;
    },

    validMultipleChoiceAnswers() {
      let valid = true;
      this.survey.questions.filter(question => question.type === "multiple_choice")
        .forEach(multipleChoiceQuestion => {
          if (!multipleChoiceQuestion.allowed_answers.every(answer => answer.value)) {
            valid = false;
          }
        });
      return valid;
    },

    canSave() {
      if (!this.survey.name) { // no title; can't submit
        return false;
      } else if (!this.survey.questions.every(surveyQuestion => surveyQuestion.text)) { // one of the questions isn't filled out; can't submit
        return false;
      } else if (!this.validMultipleChoiceAnswers) { // one of the multiple choice q's isn't filled out; can't submit
        return false;
      } else {
        return true;
      }
    }
  },

  created() {
    this.$store.dispatch("getSurvey", this.$route.params.surveyId);
  },

  components: {
    ApplicationLayout,
    DeleteModal
  }
};
</script>
